import { FilterConfigMap, FilterSection, FilterDefinition } from '../types/filters';
import { SearchFilters, SearchType } from '../types/search';
import { FILTER_OPTIONS } from '../constants/search';

// Central filter configuration that defines all aspects of each filter
export const FILTER_DEFINITIONS: Record<string, FilterDefinition> = {
  topics: {
    id: 'topics',
    titleKey: 'search.filters.topics',
    type: 'multiple' as const,
    translationPrefix: '',
    urlParam: 'topics',
    getSelectedValues: (filters: SearchFilters) => filters.topics || [],
    getApiParam: (filters: SearchFilters) => filters.topics?.length ? { topics: filters.topics } : undefined,
    parseUrlValue: (value: string) => value.split(',').filter(Boolean),
    serializeUrlValue: (value: string[]) => value.join(','),
    isVisible: (contentType: SearchType) => ['video', 'audio', 'shorts'].includes(contentType),
  },
  duration: {
    id: 'duration',
    titleKey: 'search.filters.duration',
    type: 'single' as const,
    translationPrefix: 'search.duration',
    urlParam: 'duration',
    getSelectedValues: (filters: SearchFilters) => filters.duration ? [filters.duration] : [],
    getApiParam: (filters: SearchFilters) => filters.duration ? { duration: filters.duration } : undefined,
    parseUrlValue: (value: string) => value,
    serializeUrlValue: (value: string) => value,
    isVisible: (contentType: SearchType) => ['video', 'audio'].includes(contentType),
  },
  broadcastStatus: {
    id: 'broadcastStatus',
    titleKey: 'search.filters.broadcastStatus',
    type: 'multiple' as const,
    translationPrefix: 'search.broadcastStatus',
    urlParam: 'broadcastStatus',
    getSelectedValues: (filters: SearchFilters) => filters.broadcastStatus || [],
    getApiParam: (filters: SearchFilters) => filters.broadcastStatus?.length ? { broadcastStatus: filters.broadcastStatus } : undefined,
    parseUrlValue: (value: string) => value.split(',').filter(Boolean),
    serializeUrlValue: (value: string[]) => value.join(','),
    isVisible: (contentType: SearchType) => contentType === 'lives',
    options: [
      { value: 'live', translationKey: 'search.broadcastStatus.live' },
      { value: 'upcoming', translationKey: 'search.broadcastStatus.upcoming' },
      { value: 'completed', translationKey: 'search.broadcastStatus.completed' }
    ]
  },
  sort: {
    id: 'sort',
    titleKey: 'search.filters.sortBy',
    type: 'single' as const,
    translationPrefix: 'search.sort',
    urlParam: 'sortBy',
    getSelectedValues: (filters: SearchFilters) => filters.sortBy ? [filters.sortBy] : [],
    getApiParam: (filters: SearchFilters) => {
      if (!filters.sortBy) return undefined;
      return {
        sortBy: filters.sortBy,
        ...(filters.sortType && { sortType: filters.sortType })
      };
    },
    parseUrlValue: (value: string) => value,
    serializeUrlValue: (value: string) => value,
    isVisible: () => true,
  },
} as const;

export const filterSections: FilterSection[] = Object.values(FILTER_DEFINITIONS);

export const contentTypeFilterConfig: FilterConfigMap = {
  video: {
    contentType: 'video',
    availableFilters: ['topics', 'duration', 'sort'],
    sortOptions: ['title', 'date', 'duration'],
  },
  audio: {
    contentType: 'audio',
    availableFilters: ['topics', 'duration', 'sort'],
    sortOptions: ['title', 'date', 'duration'],
  },
  shorts: {
    contentType: 'shorts',
    availableFilters: ['topics', 'sort'],
    sortOptions: ['title', 'date'],
    customConfig: {
      maxDuration: 300,
    },
  },
  lives: {
    contentType: 'lives',
    availableFilters: ['broadcastStatus', 'sort'],
    sortOptions: ['title', 'date'],
  },
  topics: {
    contentType: 'topics',
    availableFilters: ['sort'],
    sortOptions: ['title', 'date'],
  },
};

export const getAvailableFilters = (contentType: SearchType): FilterSection[] => {
  const config = contentTypeFilterConfig[contentType];
  if (!config) return [];

  return filterSections.filter(section => 
    config.availableFilters.includes(section.id) && 
    (!section.isVisible || section.isVisible(config.contentType))
  );
};

export const getSortOptions = (contentType: SearchType) => {
  const config = contentTypeFilterConfig[contentType];
  return config?.sortOptions || ['title', 'date'];
}; 